import { useState, useCallback, useRef, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useEditorShortcuts } from '@/hooks/useNoteShortcuts';
import { CollapsibleHeadings } from '@/lib/collapsibleHeadingsExtension';
import { trackNoteView } from '@/lib/recentNotes';
import PublishDialog from '@/components/PublishDialog';
import ReminderDialog from '@/components/ReminderDialog';
import NoteHistory from '@/components/NoteHistory';
import EmbedDialog from '@/components/EmbedDialog';
import EditorToolbar from '@/components/EditorToolbar';
import EditorHeaderBar from '@/components/EditorHeaderBar';
import EditorStatusBar from '@/components/EditorStatusBar';
import FreshnessBanner from '@/components/FreshnessBanner';
import InlineTagPicker from '@/components/InlineTagPicker';
import InlineNotebookPicker from '@/components/InlineNotebookPicker';
import AttachmentBar from '@/components/AttachmentBar';
import { exportNoteToPDF } from '@/lib/pdfExport';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Markdown } from 'tiptap-markdown';
import NoteLink from '@/lib/noteLinkExtension';
import { CodeBlockWithCopy } from '@/lib/codeBlockExtension';
import NoteLinkSuggestions from '@/components/NoteLinkSuggestions';
import { YouTubeEmbed, TwitterEmbed, LinkEmbed, SpotifyEmbed } from '@/lib/embedExtensions';

import { Pencil, Lock, Clock } from 'lucide-react';

export default function Editor() {
  const {
    selectedNote, selectedNoteId, setSelectedNoteId,
    updateNote, deleteNote, archiveNote, restoreNote, togglePin, toggleFavorite,
    permanentDeleteNote, activeView,
    notebooks, tags, notes, createTag,
    publishDialogRequested, setPublishDialogRequested,
    isSavedRef, staleNoteEvent, dismissStaleNote, acceptServerVersion,
  } = useApp();
  const { isEncryptionEnabled } = useAuth();

  const [showPublish, setShowPublish] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const attachmentRef = useRef(null);
  const dragCounter = useRef(0);

  // Track recently viewed notes
  useEffect(() => {
    if (selectedNote) trackNoteView(selectedNote.id, selectedNote.title);
  }, [selectedNote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle note link click
  const handleNoteLinkClick = useCallback((noteId) => {
    if (noteId) setSelectedNoteId(noteId);
  }, [setSelectedNoteId]);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false }),
      CodeBlockWithCopy,
      Placeholder.configure({ placeholder: 'Start writing... Type [[ to link to another note' }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: false }),
      Typography,
      Image.configure({ inline: true }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
      Markdown.configure({ html: false, transformPastedText: true, transformCopiedText: false }),
      NoteLink.configure({ onNoteLinkClick: handleNoteLinkClick, getNotes: () => notes.filter((n) => !n.is_deleted) }),
      YouTubeEmbed, TwitterEmbed, LinkEmbed, SpotifyEmbed,
      CollapsibleHeadings,
    ],
    editorProps: {
      attributes: {
        class: 'tiptap-editor outline-none min-h-full prose prose-invert prose-sm sm:prose-base max-w-none',
        'data-testid': 'editor-content-tiptap',
      },
    },
    onUpdate: ({ editor: ed }) => handleContentChange(ed.storage.markdown.getMarkdown()),
  });

  // Autosave hook
  const { title, saving, saved, handleTitleChange, handleContentChange, handleForceSave } = useAutoSave({
    selectedNote, selectedNoteId, editor, updateNote,
  });

  // Keep isSavedRef in sync for cross-device freshness detection
  useEffect(() => { isSavedRef.current = saved; }, [saved, isSavedRef]);

  // Keyboard shortcuts
  useEditorShortcuts(editor, {
    onSave: handleForceSave,
    onShare: () => setShowPublish(true),
    onExportPDF: () => selectedNote && exportNoteToPDF(selectedNote),
  });

  // Clear collapsed headings on note switch
  useEffect(() => {
    if (editor && selectedNoteId) editor.commands.clearCollapsed?.();
  }, [selectedNoteId, editor]);

  // Open publish dialog when requested from NoteList
  useEffect(() => {
    if (publishDialogRequested && selectedNote) {
      setShowPublish(true);
      setPublishDialogRequested(false);
    }
  }, [publishDialogRequested, selectedNote, setPublishDialogRequested]);

  // Editor actions
  const insertTable = () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url && editor) editor.chain().focus().setImage({ src: url }).run();
  };
  const handleInsertEmbed = (embedData) => {
    if (!editor) return;
    switch (embedData.type) {
      case 'youtube': editor.chain().focus().setYouTubeVideo({ videoId: embedData.videoId }).run(); break;
      case 'twitter': editor.chain().focus().setTwitterEmbed({ tweetId: embedData.tweetId, tweetUrl: embedData.tweetUrl }).run(); break;
      case 'spotify': editor.chain().focus().setSpotifyEmbed({ spotifyUri: embedData.spotifyUri, type: embedData.spotifyType }).run(); break;
      case 'link': editor.chain().focus().setLinkEmbed({ url: embedData.url, domain: embedData.domain }).run(); break;
      default: break;
    }
  };
  const handleImageInsert = useCallback((url, altText) => {
    if (editor) editor.chain().focus().setImage({ src: url, alt: altText || 'attachment' }).run();
  }, [editor]);

  // Drag-and-drop file upload
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; if (e.dataTransfer.types.includes('Files')) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); dragCounter.current = 0; setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && attachmentRef.current) {
      files.length === 1 ? attachmentRef.current.uploadFile(files[0]) : attachmentRef.current.uploadFiles(files);
    }
  };

  // ── Empty / locked states ─────────────────────────────────────
  if (!selectedNote) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-background" data-testid="editor-empty">
        <div className="text-center px-6">
          <Pencil className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Select a note or create a new one</p>
          <p className="text-xs text-muted-foreground/50 mt-1">Ctrl+N to create, Ctrl+K to search</p>
        </div>
      </div>
    );
  }

  if (selectedNote._decryptFailed || selectedNote._needsDecryption) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-background" data-testid="editor-decrypt-failed">
        <div className="text-center px-6">
          <Lock className="w-12 h-12 text-accent-amber/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {selectedNote._decryptFailed ? 'Unable to decrypt this note' : 'Waiting for encryption key...'}
          </p>
          <p className="text-xs text-muted-foreground/50 mt-1.5">
            {selectedNote._decryptFailed ? 'The encryption key may have changed. Try logging out and back in.' : 'Please wait while the encryption key is loaded.'}
          </p>
        </div>
      </div>
    );
  }

  const noteNotebook = notebooks.find((nb) => nb.id === selectedNote.notebook_id);
  const wordCount = editor ? editor.getText().split(/\s+/).filter(Boolean).length : 0;
  const charCount = editor ? editor.getText().length : 0;
  const isJournalEntry = selectedNote.is_journal;

  return (
    <div
      className="flex flex-col h-full w-full bg-background"
      data-testid="editor-panel"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Top Bar */}
      <EditorHeaderBar
        selectedNote={selectedNote}
        selectedNoteId={selectedNoteId}
        setSelectedNoteId={setSelectedNoteId}
        isJournalEntry={isJournalEntry}
        activeView={activeView}
        noteNotebook={noteNotebook}
        togglePin={togglePin}
        toggleFavorite={toggleFavorite}
        archiveNote={archiveNote}
        deleteNote={deleteNote}
        restoreNote={restoreNote}
        permanentDeleteNote={permanentDeleteNote}
        setShowPublish={setShowPublish}
        setShowReminder={setShowReminder}
        setShowHistory={setShowHistory}
      />

      {/* Freshness conflict banner */}
      {staleNoteEvent && staleNoteEvent.note_id === selectedNoteId && (
        <FreshnessBanner
          onReload={acceptServerVersion}
          onDismiss={dismissStaleNote}
        />
      )}

      {/* Formatting Toolbar */}
      <EditorToolbar
        editor={editor}
        onInsertImage={insertImage}
        onInsertTable={insertTable}
        onShowEmbed={() => setShowEmbed(true)}
        onAttach={() => attachmentRef.current?.click()}
      />

      {/* Title + Inline Controls */}
      <div className="px-4 pt-4 sm:px-8 sm:pt-6 lg:px-16">
        {isJournalEntry ? (
          <>
            <div className="flex items-center gap-2 text-muted-foreground mb-1" data-testid="editor-journal-date">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">
                {(() => {
                  try { return new Date(selectedNote.created_at).toLocaleString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
                  catch { return selectedNote.entry_date || ''; }
                })()}
              </span>
            </div>
            <input type="text" value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Untitled"
              className="w-full bg-transparent text-base sm:text-lg text-muted-foreground/70 font-medium tracking-tight outline-none placeholder:text-muted-foreground/20" data-testid="editor-title-input" />
          </>
        ) : (
          <input type="text" value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Untitled"
            className="w-full bg-transparent text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/30" data-testid="editor-title-input" />
        )}
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="editor-inline-controls">
          {!isJournalEntry && <InlineNotebookPicker selectedNote={selectedNote} notebooks={notebooks} updateNote={updateNote} selectedNoteId={selectedNoteId} />}
          <InlineTagPicker selectedNote={selectedNote} tags={tags} updateNote={updateNote} selectedNoteId={selectedNoteId} createTag={createTag} />
        </div>
      </div>

      {/* Attachments */}
      <AttachmentBar noteId={selectedNoteId} onImageInsert={handleImageInsert} ref={attachmentRef} />

      {/* TipTap Editor Body */}
      <div className={`flex-1 min-h-0 overflow-auto px-4 py-3 sm:px-8 sm:py-4 lg:px-16 relative transition-colors ${isDragging ? 'bg-accent-amber/5' : ''}`}>
        {isDragging && (
          <div className="absolute inset-4 border-2 border-dashed border-accent-amber/50 rounded-xl flex items-center justify-center bg-accent-amber/5 z-10 pointer-events-none" data-testid="editor-drop-zone">
            <div className="text-center">
              <p className="text-sm font-medium text-accent-amber">Drop files to attach</p>
              <p className="text-xs text-muted-foreground mt-1">Images, documents, audio, video — multiple files supported</p>
            </div>
          </div>
        )}
        <EditorContent editor={editor} className="h-full" />
        <NoteLinkSuggestions editor={editor} />
      </div>

      {/* Bottom Bar */}
      <EditorStatusBar
        isJournalEntry={isJournalEntry}
        selectedNote={selectedNote}
        isEncryptionEnabled={isEncryptionEnabled}
        wordCount={wordCount}
        charCount={charCount}
        saving={saving}
        saved={saved}
        setShowPublish={setShowPublish}
      />

      {/* Dialogs */}
      {showPublish && <PublishDialog note={selectedNote} editor={editor} onClose={() => setShowPublish(false)} />}
      {showReminder && <ReminderDialog noteId={selectedNoteId} onClose={() => setShowReminder(false)} />}
      {showHistory && <NoteHistory noteId={selectedNoteId} onClose={() => setShowHistory(false)} />}
      {showEmbed && <EmbedDialog open={showEmbed} onClose={() => setShowEmbed(false)} onInsert={handleInsertEmbed} />}
    </div>
  );
}
